import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  User
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase/config';
import toast from 'react-hot-toast';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [userRole, setUserRole] = useState(null);

  // Function to check user role from Firestore
  const checkUserRole = async (user) => {
    if (!user) {
      setUserRole(null);
      setIsAdmin(false);
      return false;
    }

    try {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        
        // Debug: Log the user data and role structure
        console.log('User data:', userData);
        console.log('Role field:', userData.role);
        console.log('Role type:', typeof userData.role);
        console.log('Is array:', Array.isArray(userData.role));
        
        // Handle role as an array of strings - extract the actual role value
        let role = 'user'; // Default to 'user' (no access)
        if (userData.role) {
          if (Array.isArray(userData.role)) {
            // Role is an array - check if it contains 'admin' or 'author'
            console.log('Role array contents:', userData.role);
            if (userData.role.includes('admin')) {
              role = 'admin';
              console.log('Found admin role in array');
            } else if (userData.role.includes('author')) {
              role = 'author';
              console.log('Found author role in array');
            } else {
              console.log('No valid role found in array');
            }
          } else if (typeof userData.role === 'string') {
            // Role is a simple string (fallback)
            role = userData.role;
            console.log('Role is string:', role);
          } else if (typeof userData.role === 'object') {
            // Role is a map/object - try common patterns (fallback)
            console.log('Role object keys:', Object.keys(userData.role));
            console.log('Role object values:', Object.values(userData.role));
            
            // Try to find any string value that could be a role
            const roleValues = Object.values(userData.role);
            const stringRole = roleValues.find(val => typeof val === 'string' && (val === 'admin' || val === 'author'));
            
            if (stringRole) {
              role = stringRole;
              console.log('Found valid role in object:', role);
            } else {
              // Fallback to common patterns
              role = userData.role.role || 
                     userData.role.type || 
                     userData.role.name || 
                     userData.role.value || 
                     'user';
              console.log('Using fallback role:', role);
            }
          }
        }
        
        console.log('Final extracted role:', role);
        setUserRole(role);
        
        // Only allow admin and author roles
        const hasAccess = role === 'admin' || role === 'author';
        // isAdmin should only be true for actual admin users, not all users with access
        setIsAdmin(role === 'admin');
        
        if (!hasAccess) {
          toast.error('Access denied. Only admin and author users can access this panel.');
          await signOut(auth);
          return false;
        }
        
        return hasAccess;
      } else {
        // User document doesn't exist in Firestore
        toast.error('Access denied. User profile not found.');
        await signOut(auth);
        setUserRole(null);
        setIsAdmin(false);
        return false;
      }
    } catch (error) {
      console.error('Error checking user role:', error);
      toast.error('Error verifying user permissions. Please try again.');
      await signOut(auth);
      setUserRole(null);
      setIsAdmin(false);
      return false;
    }
  };

  const signIn = async (email, password) => {
    try {
      setLoading(true);
      const result = await signInWithEmailAndPassword(auth, email, password);
      
      // Check user role after successful authentication
      const hasAccess = await checkUserRole(result.user);
      
      if (hasAccess) {
        toast.success('Successfully signed in!');
        return result;
      } else {
        // User will be automatically signed out by checkUserRole if access is denied
        return null;
      }
    } catch (error) {
      let errorMessage = 'Failed to sign in';
      
      switch (error.code) {
        case 'auth/user-not-found':
          errorMessage = 'No account found with this email';
          break;
        case 'auth/wrong-password':
          errorMessage = 'Incorrect password';
          break;
        case 'auth/invalid-email':
          errorMessage = 'Invalid email address';
          break;
        case 'auth/too-many-requests':
          errorMessage = 'Too many failed attempts. Please try again later';
          break;
        case 'auth/user-disabled':
          errorMessage = 'This account has been disabled';
          break;
        default:
          errorMessage = error.message;
      }
      
      toast.error(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      setIsAdmin(false);
      setUserRole(null);
      toast.success('Successfully signed out!');
    } catch (error) {
      toast.error('Failed to sign out');
      console.error('Logout error:', error);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      
      if (user) {
        // Check user role when auth state changes
        await checkUserRole(user);
      } else {
        setUserRole(null);
        setIsAdmin(false);
      }
      
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    isAdmin,
    userRole,
    loading,
    signIn,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
